<script setup>
// NOTICE: KAPTION-INTEGRATION
// rumo-app-updates.MARK.1-5, POPOVER.1-3
//
// The Rumo mark at the top of both sidebars. Up to date, it is exactly the logo
// button it replaced and still toggles the sidebar. When the Rumo on screen is
// out of date it becomes the place to fix that: Refresh for a newer web bundle in
// production, Update for a downloaded Rumo Desktop app.
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Logo from 'next/icon/Logo.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useKaptionAppUpdates } from 'dashboard/composables/useKaptionAppUpdates';

const props = defineProps({
  // The 60px sidebar: no room for a label, so the mark keeps its size.
  compact: { type: Boolean, default: false },
});

const emit = defineEmits(['toggleSidebarVersion']);

const POPOVER_GAP = 8;
const VIEWPORT_PADDING = 8;
const RING_CIRCUMFERENCE = 2 * Math.PI * 16;

const { t } = useI18n();
const isRTL = useMapGetter('accounts/isRTL');
const {
  mode,
  busyAction,
  isSnoozed,
  availableVersion,
  currentVersion,
  downloadPercent,
  refreshNow,
  restartToUpdate,
  retryUpdate,
  later,
} = useKaptionAppUpdates();

const trigger = ref(null);
const popover = ref(null);
const isOpen = ref(false);
const popoverStyle = ref({});

const titleId = computed(
  () => `kaption-update-title-${props.compact ? 'compact' : 'full'}`
);

const isPending = computed(() => ['refresh', 'update'].includes(mode.value));
const isActionable = computed(() =>
  ['refresh', 'update', 'downloading', 'error'].includes(mode.value)
);

// rumo-app-updates.MARK.2, MARK.5
const showPill = computed(
  () =>
    !props.compact &&
    ((isPending.value && !isSnoozed.value) || mode.value === 'busy')
);

const pillLabel = computed(() => {
  if (mode.value === 'busy') {
    return busyAction.value === 'restart'
      ? t('KAPTION_APP_UPDATES.RESTARTING')
      : t('KAPTION_APP_UPDATES.REFRESHING');
  }
  return mode.value === 'update'
    ? t('KAPTION_APP_UPDATES.UPDATE_LABEL')
    : t('KAPTION_APP_UPDATES.REFRESH_LABEL');
});

const triggerLabel = computed(() => {
  switch (mode.value) {
    case 'refresh':
      return t('KAPTION_APP_UPDATES.REFRESH_AVAILABLE');
    case 'update':
      return t('KAPTION_APP_UPDATES.UPDATE_READY');
    case 'downloading':
      return t('KAPTION_APP_UPDATES.DOWNLOADING_LABEL', {
        percent: downloadPercent.value,
      });
    case 'error':
      return t('KAPTION_APP_UPDATES.ERROR_TITLE');
    case 'busy':
      return pillLabel.value;
    default:
      return props.compact
        ? t('SIDEBAR.EXPAND_SIDEBAR')
        : t('SIDEBAR.COLLAPSE_SIDEBAR');
  }
});

const triggerClass = computed(() => {
  if (showPill.value) {
    return 'flex h-6 items-center gap-1.5 rounded-full ps-1 pe-2.5 bg-n-brand/10 text-n-brand outline outline-1 -outline-offset-1 outline-n-brand/25 hover:bg-n-brand/15';
  }
  if (props.compact) {
    // rumo-app-updates.MARK.3 — the halo marks the collapsed sidebar's mark.
    return [
      'grid size-10 place-content-center rounded-full hover:bg-n-alpha-2',
      isPending.value ? 'ring-2 ring-n-brand/30' : '',
    ].join(' ');
  }
  return 'grid size-6 place-content-center rounded-lg hover:bg-n-alpha-2';
});

const dotClass = computed(() => {
  let color = null;
  if (isPending.value) color = 'bg-[#FF684C]';
  if (mode.value === 'error') color = 'bg-n-amber-9';
  if (!color) return null;
  const placement = props.compact
    ? 'top-0 size-2.5 ltr:right-0 rtl:left-0'
    : '-top-1 size-2 ltr:-right-1 rtl:-left-1';
  return `pointer-events-none absolute rounded-full ring-2 ring-n-solid-2 ${placement} ${color}`;
});

const ringDashOffset = computed(
  () => RING_CIRCUMFERENCE * (1 - downloadPercent.value / 100)
);

const popoverTitle = computed(() => {
  switch (mode.value) {
    case 'update':
      return availableVersion.value
        ? t('KAPTION_APP_UPDATES.UPDATE_TITLE', {
            version: availableVersion.value,
          })
        : t('KAPTION_APP_UPDATES.UPDATE_TITLE_GENERIC');
    case 'downloading':
      return availableVersion.value
        ? t('KAPTION_APP_UPDATES.DOWNLOADING_TITLE', {
            version: availableVersion.value,
          })
        : t('KAPTION_APP_UPDATES.DOWNLOADING_TITLE_GENERIC');
    case 'error':
      return t('KAPTION_APP_UPDATES.ERROR_TITLE');
    default:
      return t('KAPTION_APP_UPDATES.REFRESH_TITLE');
  }
});

const popoverBody = computed(() => {
  switch (mode.value) {
    case 'update':
      return t('KAPTION_APP_UPDATES.UPDATE_BODY');
    case 'downloading':
      return t('KAPTION_APP_UPDATES.DOWNLOADING_BODY', {
        percent: downloadPercent.value,
      });
    case 'error':
      return t('KAPTION_APP_UPDATES.ERROR_BODY');
    default:
      return t('KAPTION_APP_UPDATES.REFRESH_BODY');
  }
});

const primaryAction = computed(() => {
  switch (mode.value) {
    case 'refresh':
      return {
        label: t('KAPTION_APP_UPDATES.REFRESH_ACTION'),
        icon: 'i-lucide-refresh-ccw',
        run: refreshNow,
      };
    case 'update':
      return {
        label: t('KAPTION_APP_UPDATES.UPDATE_ACTION'),
        icon: 'i-lucide-power',
        run: restartToUpdate,
      };
    case 'error':
      return {
        label: t('KAPTION_APP_UPDATES.TRY_AGAIN'),
        icon: 'i-lucide-rotate-cw',
        run: retryUpdate,
      };
    default:
      return null;
  }
});

// rumo-app-updates.POPOVER.1 — the sidebar card clips its content under Warm
// Paper, so the popover is teleported and placed against the viewport.
const placePopover = () => {
  const el = trigger.value;
  if (!el) return;
  const rect = el.getBoundingClientRect();
  const clamp = value => `${Math.max(VIEWPORT_PADDING, value)}px`;

  if (props.compact) {
    const top = clamp(rect.top);
    popoverStyle.value = isRTL.value
      ? { top, right: clamp(window.innerWidth - rect.left + POPOVER_GAP) }
      : { top, left: clamp(rect.right + POPOVER_GAP) };
    return;
  }

  const top = `${rect.bottom + 6}px`;
  popoverStyle.value = isRTL.value
    ? { top, right: clamp(window.innerWidth - rect.right) }
    : { top, left: clamp(rect.left) };
};

const closePopover = (returnFocus = false) => {
  if (!isOpen.value) return;
  isOpen.value = false;
  window.removeEventListener('resize', placePopover);
  if (returnFocus === true) trigger.value?.focus();
};

const openPopover = async () => {
  placePopover();
  isOpen.value = true;
  window.addEventListener('resize', placePopover);
  await nextTick();
  // Focus lands inside the popover so Escape reaches it (POPOVER.3). The
  // downloading popover has no buttons, so it takes focus itself.
  const target = popover.value?.querySelector('button') || popover.value;
  target?.focus();
};

// rumo-app-updates.MARK.1, MARK.4
const onMarkClick = () => {
  if (mode.value === 'busy') return;
  if (!isActionable.value) {
    emit('toggleSidebarVersion');
    return;
  }
  if (isOpen.value) closePopover();
  else openPopover();
};

const onPrimary = () => {
  const action = primaryAction.value;
  closePopover();
  action?.run();
};

const onLater = () => {
  if (isPending.value) later();
  closePopover(true);
};

watch(mode, next => {
  if (next === 'idle' || next === 'busy') closePopover();
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', placePopover);
});
</script>

<template>
  <div class="flex-shrink-0">
    <button
      ref="trigger"
      v-tooltip.right="compact && isActionable ? triggerLabel : null"
      type="button"
      data-testid="kaption-update-mark"
      :data-update-mode="mode"
      :class="triggerClass"
      :aria-label="triggerLabel"
      :aria-haspopup="isActionable ? 'dialog' : undefined"
      :aria-expanded="isActionable ? String(isOpen) : undefined"
      :aria-busy="mode === 'busy' ? 'true' : undefined"
      @click="onMarkClick"
      @keydown.esc="closePopover(true)"
    >
      <span
        class="relative grid place-content-center"
        :class="compact ? 'size-8' : 'size-4'"
      >
        <span
          v-if="mode === 'busy'"
          aria-hidden="true"
          class="i-lucide-loader-circle animate-spin text-n-brand"
          :class="compact ? 'size-6' : 'size-4'"
        />
        <Logo
          v-else
          class="!max-w-none"
          :class="compact ? 'size-8' : 'size-4'"
        />
        <svg
          v-if="mode === 'downloading'"
          data-testid="kaption-update-progress"
          aria-hidden="true"
          viewBox="0 0 36 36"
          class="absolute pointer-events-none text-n-brand"
          :class="compact ? '-inset-1' : '-inset-[3px]'"
        >
          <circle
            cx="18"
            cy="18"
            r="16"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            opacity="0.2"
          />
          <circle
            cx="18"
            cy="18"
            r="16"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            transform="rotate(-90 18 18)"
            :stroke-dasharray="RING_CIRCUMFERENCE"
            :stroke-dashoffset="ringDashOffset"
          />
        </svg>
        <span
          v-if="dotClass"
          data-testid="kaption-update-dot"
          :class="dotClass"
        />
      </span>
      <span
        v-if="showPill"
        class="text-xs font-semibold leading-4 whitespace-nowrap"
      >
        {{ pillLabel }}
      </span>
    </button>

    <TeleportWithDirection v-if="isOpen" to="body">
      <div
        ref="popover"
        v-on-clickaway="closePopover"
        role="dialog"
        tabindex="-1"
        data-testid="kaption-update-popover"
        :aria-labelledby="titleId"
        class="fixed z-[9999] flex w-[19rem] flex-col gap-3 rounded-xl bg-n-solid-2 p-3.5 shadow-lg outline outline-1 outline-n-weak"
        :style="popoverStyle"
        @keydown.esc.stop="closePopover(true)"
      >
        <div class="flex items-start gap-2.5">
          <span
            class="grid flex-shrink-0 rounded-full size-8 place-content-center"
            :class="
              mode === 'error'
                ? 'bg-n-amber-3 text-n-amber-11'
                : 'bg-n-brand/10 text-n-brand'
            "
          >
            <span
              v-if="mode === 'downloading'"
              aria-hidden="true"
              class="i-lucide-download size-4"
            />
            <span
              v-else-if="mode === 'error'"
              aria-hidden="true"
              class="i-lucide-circle-alert size-4"
            />
            <Logo v-else class="size-5 !max-w-none" />
          </span>
          <div class="flex flex-col min-w-0 gap-1 grow">
            <h2
              :id="titleId"
              class="m-0 text-sm font-semibold leading-5 text-n-slate-12"
            >
              {{ popoverTitle }}
            </h2>
            <div
              v-if="mode === 'downloading'"
              role="progressbar"
              aria-valuemin="0"
              aria-valuemax="100"
              :aria-valuenow="downloadPercent"
              :aria-label="popoverTitle"
              class="h-1.5 my-1 overflow-hidden rounded-full bg-n-brand/15"
            >
              <div
                class="h-full rounded-full bg-n-brand"
                :style="{ width: `${downloadPercent}%` }"
              />
            </div>
            <p class="m-0 text-[13px] leading-[19px] text-n-slate-11">
              {{ popoverBody }}
            </p>
            <p
              v-if="mode === 'update' && currentVersion"
              class="m-0 text-xs leading-4 text-n-slate-11"
            >
              {{
                t('KAPTION_APP_UPDATES.CURRENT_VERSION', {
                  version: currentVersion,
                })
              }}
            </p>
          </div>
        </div>
        <div
          v-if="primaryAction"
          class="flex items-center gap-2 ltr:pl-[42px] rtl:pr-[42px]"
        >
          <Button
            size="sm"
            :label="primaryAction.label"
            :icon="primaryAction.icon"
            @click="onPrimary"
          />
          <Button
            size="sm"
            variant="ghost"
            color="slate"
            :label="t('KAPTION_APP_UPDATES.LATER')"
            @click="onLater"
          />
        </div>
      </div>
    </TeleportWithDirection>
  </div>
</template>
